import { Form, Modal, Spin, notification } from 'antd';
import { ActionControls, StepControls } from 'components/Create/common';
import { DeploymentRequestInfo } from 'components/Create/common/DeploymentRequestInfo';
import { SparingRequestConfirmation } from 'components/Create/common/SparingtRequestConfirmation.tsx';
import { Loader } from 'components/common/Loader';
import { PostedLineItemsModal } from 'components/common/PostedLineItemsModal';
import { FormikProvider, useFormik } from 'formik';
import { QueryErrorModel } from 'models/ErrorModel';
import { DefectiveReturnItemPayload, DefectiveReturnsPayload, SparingRequest, SparingRequestPayload } from 'models/SparingRequests';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { useAddUserToSmsNotificationsMutation, useCreateAttachmentMutation, useCreateSparingRequestMutation, usePostSparingRequestMutation } from 'redux/services/twittlebugs/sparingRequestsApi';
import { resetAttachments } from 'redux/slices/attachmentsSlice';
import { setFullOutageAddress, setFullShippingAddress, setIsExit, setIsPost } from 'redux/slices/formSlice';
import { useAppSelector } from 'redux/store';
import { getDefectiveReturnsRequiredInfoErrors, isDefectiveReturnsInformationRequirementActive, renumberDefectiveReturnItems } from 'utils/defectiveReturns';
import { mapSlaTypeForBackend } from 'utils/slaMapping';

export const CreateDeploymentRequestLayout = (): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { user, postRequestLoadingMessage } = useAppSelector((state) => state.app);
  const { isExit, isPost } = useAppSelector((state) => state.form);
  const { attachments } = useAppSelector((state) => state.attachments);
  const nav = useNavigate();

  const [createAttachment] = useCreateAttachmentMutation();
  const [addUserToSmsNotifications] = useAddUserToSmsNotificationsMutation();
  const [createSparing] = useCreateSparingRequestMutation();
  const [postSparingRequest, { data: postedData, isSuccess }] = usePostSparingRequestMutation();
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);

  const dispatch = useDispatch();

  const [createSparingResponse, setCreateSparingResponse] = useState<SparingRequest | undefined>(undefined);

  const userNumber = user?.['https://acuity.mdsiinc.com/user/user_metadata']?.phone_number;
  const { data: requestTypesData } = useGetSparingRequestConfigurationTypesQuery();

  const formik = useFormik<SparingRequestPayload>({
    enableReinitialize: true,
    initialValues: {
      requestNumber: '',
      customerReferenceNumber: '',
      customerPurchaseOrderNumber: '',
      endCustomer: '',
      sparingRequestTypeId: '',
      shippingLocationId: '',
      lineItems: [],
      outageLocationId: '',
      contactEmail: '',
      contactName: '',
      contactPhoneNumber: '',
      requestedDeliveryDate: '',
      notificationEmails: user && user.email ? [user.email] : [],
      notes: '',
      isLabConfigurationRequired: false,
      isUserOptedIn: false,
      defectiveReturns: undefined
    },
    validate: (values) => {
      const errors: any = {};
      const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);

      if (selectedType) {
        if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
          errors.customerReferenceNumber = `${selectedType.reference1Label || 'Primary Reference Number'} is required`;
        }

        if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
          errors.customerPurchaseOrderNumber = `${selectedType.reference2Label || 'Secondary Reference Number'} is required`;
        }

        if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
          errors.endCustomer = 'End Customer is required';
        }

        // MDSI1-4304: Return Part Number / Return Serial Number on defective returns. Only a Post is
        // blocked - formik refuses to submit at all while validate returns anything, and a draft has
        // to stay saveable, so this error is raised only for the submit that actually posts.
        const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
          items: values.defectiveReturns?.items,
          lineItems: values.lineItems,
          requestType: selectedType
        });

        // message is set exactly when the helper is not satisfied; guarding on it keeps an
        // undefined from ever landing in errors, which formik would treat as a silent block.
        if (isPost && defectiveReturnsRequiredInfo.message) {
          errors.defectiveReturns = defectiveReturnsRequiredInfo.message;
        }
      }

      // Enforce 10-digit phone number
      const phoneDigits = (values.contactPhoneNumber || '').replace(/\D/g, '');

      if (phoneDigits.length !== 10) {
        errors.contactPhoneNumber = 'Phone number must have 10 digits';
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      // Validate required fields based on sparing request type configuration
      const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);

      if (selectedType) {
        const missingFields: string[] = [];

        if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
          missingFields.push(selectedType.reference1Label || 'Primary Reference Number');
          setFieldError('customerReferenceNumber', `${selectedType.reference1Label || 'Primary Reference Number'} is required`);
        }

        if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
          missingFields.push(selectedType.reference2Label || 'Secondary Reference Number');
          setFieldError('customerPurchaseOrderNumber', `${selectedType.reference2Label || 'Secondary Reference Number'} is required`);
        }

        if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
          missingFields.push('End Customer');
          setFieldError('endCustomer', 'End Customer is required');
        }

        // MDSI1-4304: Return Part Number / Return Serial Number on defective returns, Post only.
        const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
          items: values.defectiveReturns?.items,
          lineItems: values.lineItems,
          requestType: selectedType
        });

        if (isPost && defectiveReturnsRequiredInfo.missingFieldsSummary && defectiveReturnsRequiredInfo.message) {
          missingFields.push(defectiveReturnsRequiredInfo.missingFieldsSummary);
          setFieldError('defectiveReturns', defectiveReturnsRequiredInfo.message);
        }

        // Contact phone validation
        const phoneDigits = (values.contactPhoneNumber || '').replace(/\D/g, '');

        if (phoneDigits.length !== 10) {
          missingFields.push('Contact Phone (10 digits)');
        }

        if (missingFields.length > 0) {
          notification.error({
            message: 'Required Fields Missing',
            description: `Please fill in the following required fields: ${missingFields.join(', ')}`,
            className: 'custom-class',
            style: {
              width: 600
            },
            duration: 5
          });

          setSubmitting(false);

          // This submit attempt is abandoned - a later Save must not be mistaken for a Post.
          dispatch(setIsPost(false));
          dispatch(setIsExit(false));

          return;
        }
      }

      setLoadingMessage('Creating sparing request...');
      // Respect API flag for defective returns activation
      const selectedTypeForPayload = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);
      const shouldIncludeDefectiveReturns = Boolean(selectedTypeForPayload?.isActivateDefectiveReturns);

      // MDSI1-4304 gate. With the requirement OFF this builds the items exactly as main does - from
      // lineItems, with blank serial/case/rma numbers and used true - so whatever was typed into the
      // Defective Returns table on Create is discarded. That discard is PRE-EXISTING behaviour, left
      // unchanged on purpose by MDSI1-4304. With the requirement ON the typed items have to be sent
      // (matched to the line items by product and renumbered), or the requirement would be vacuous.
      const isDefectiveReturnsInfoRequired = isDefectiveReturnsInformationRequirementActive(selectedTypeForPayload);

      const payload = {
        ...values,
        lineItems: values.lineItems.map((item, idx) => ({
          productNumber: item.productNumber,
          lineNumber: idx + 1,
          quantity: item.quantity,
          subLines: item.subLines?.map((subLine) => ({
            ...subLine,
            serviceLevelAgreement: mapSlaTypeForBackend(subLine.serviceLevelAgreement)
          }))
        })),
        defectiveReturns: shouldIncludeDefectiveReturns
          ? ({
              contactName: values.contactName ?? '',
              contactPhoneNumber: values.contactPhoneNumber ?? '',
              contactEmail: values.contactEmail ?? '',
              waybillDeliveryMethods: undefined,
              items: isDefectiveReturnsInfoRequired
                ? renumberDefectiveReturnItems(values.defectiveReturns?.items, values.lineItems)
                : values.lineItems.flatMap((item, idx) => {
                    const quantity = item.quantity;
                    const items: DefectiveReturnItemPayload[] = [];

                    for (let i = 0; i < quantity; i++) {
                      items.push({
                        lineNumber: idx + 1,
                        productNumber: item.productNumber,
                        serialNumber: '',
                        caseNumber: '',
                        rmaNumber: '',
                        quantity: 1,
                        used: true // Default to true since "Didn't Use" checkbox is unchecked by default
                      });
                    }

                    return items;
                  })
            } as DefectiveReturnsPayload)
          : undefined
      };

      let resp;

      try {
        const response = await createSparing(payload).unwrap();

        if (values.isUserOptedIn) {
          await addUserToSmsNotifications({ id: response.id, payload: {} }).unwrap();
        }

        resp = response;

        if (attachments.length) {
          setLoadingMessage('Creating attachments...');
          for await (const attachment of attachments) {
            const formData = new FormData();

            formData.append(`${attachment.name}`, attachment);
            await createAttachment({ file: formData, id: response.id }).unwrap();
          }
        }

        if (isPost) {
          setLoadingMessage(undefined);
          await postSparingRequest({ sparingResponse: response, sparingRequestId: response.id }).unwrap();

          dispatch(setIsPost(false));
        } else setCreateSparingResponse(response);
        dispatch(resetAttachments());
      } catch (e) {
        if (resp?.id) nav(`/edit/${resp.id}`);
        setLoadingMessage(undefined);
        console.error(e, 'error');

        const error = e as QueryErrorModel;
        const errorMessage = error?.data?.errorMessage ? error?.data?.errorMessage : error.data ? (error?.data as string) : 'An error occured, the team has been notified.';

        dispatch(setIsPost(false));

        notification.error({
          message: 'Error',
          description: errorMessage,
          className: 'custom-class',
          style: {
            width: 600
          },
          duration: 5
        });
      }
    }
  });

  const handleClose = (): void => {
    if (isExit) nav('/');
    else nav(`/edit/${createSparingResponse?.id}`);
    setCreateSparingResponse(undefined);
    dispatch(setIsExit(false));
  };

  /* ******************** Effects ******************** */
  useEffect(() => {
    return () => {
      dispatch(setFullOutageAddress(null));
      dispatch(setFullShippingAddress(null));
      dispatch(resetAttachments());
      // Leaving the form abandons any submit attempt in flight - never carry a Post into the next one.
      dispatch(setIsPost(false));
      dispatch(setIsExit(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if (requestConfigError) {
  //   <NoConfigType messageContent="is not configured for Sparing Requests. Please contact MDSi support." />;
  // }

  // if (requestTypeError || (requestTypesData && requestTypesData.totalCount === 0)) {
  //   return <NoConfigType messageContent="has no deployment request types created. Please contact MDSi support." />;
  // }

  return (
    <>
      <FormikProvider value={formik}>
        <Spin indicator={<Loader loadingMessage={postRequestLoadingMessage ?? loadingMessage} />} spinning={formik.isSubmitting}>
          <Form layout="vertical">
            <ActionControls />
            <DeploymentRequestInfo />
            <StepControls />
            <Outlet />
          </Form>
        </Spin>
      </FormikProvider>
      <Modal
        onOk={handleClose}
        open={createSparingResponse !== undefined}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={handleClose}
        closeIcon={undefined}
        bodyStyle={{ background: 'linear-gradient(to right bottom, #979797, #acacac, #c1c1c1, #d7d7d7, #ededed)', padding: 0, height: 400, position: 'relative', borderRadius: 5 }}>
        <SparingRequestConfirmation confirmationInfo={createSparingResponse} />
      </Modal>
      <PostedLineItemsModal open={isSuccess} submittedSparingRequest={postedData?.data} />
    </>
  );
};
